import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Plus, Users } from "lucide-react";
import { api } from "@/lib/api";
import { ROLE_LABELS, formatDate } from "@/lib/labels";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import UserModuleGrants from "@/components/admin/UserModuleGrants";
import {
  EmptyState,
  LoadingState,
  PageHeader,
  SearchBox,
  StatePill,
  apiErrorMessage,
} from "@/components/admin/AdminUI";

export function userClientIds(user) {
  return [...new Set([...(user.client_ids || []), user.client_id, ...((user.scope || {}).client_ids || [])].filter(Boolean))];
}

export function userStatus(user) {
  if (user.blocked) return { tone: "danger", label: "Bloqueado" };
  if (user.active === false) return { tone: "muted", label: "Inativo" };
  return { tone: "success", label: "Ativo" };
}

const STEPS = ["Identificação", "Empresa", "Perfil", "Módulos", "Revisão"];

function strongPassword() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  const pick = () => chars[Math.floor(Math.random() * chars.length)];
  return `${Array.from({ length: 10 }, pick).join("")}!${Math.floor(Math.random() * 90 + 10)}`;
}

function CreateUserWizard({ open, onOpenChange, clients, roles, defaultClient, onCreated }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({});
  const [units, setUnits] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setForm({ name: "", email: "", password: strongPassword(), client_id: defaultClient || "", unit_ids: [], role: "", modules: {} });
  }, [open, defaultClient]);

  useEffect(() => {
    if (!form.client_id) return setUnits([]);
    api.get("/units", { params: { client_id: form.client_id } })
      .then(({ data }) => setUnits(Array.isArray(data) ? data : data.items || []))
      .catch(() => setUnits([]));
  }, [form.client_id]);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));
  const clientName = clients.find((c) => c.id === form.client_id)?.name;
  const valid = [
    form.name?.trim().length >= 2 && /\S+@\S+\.\S+/.test(form.email || "") && (form.password || "").length >= 8,
    Boolean(form.client_id) || form.role === "admin",
    Boolean(form.role),
    true,
    true,
  ];

  const submit = async () => {
    setSaving(true);
    try {
      const clientIds = form.client_id ? [form.client_id] : [];
      const { data } = await api.post("/users", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        role: form.role,
        client_id: form.client_id || null,
        client_ids: clientIds,
        unit_ids: form.unit_ids,
        unit_id: form.unit_ids[0] || null,
        modules: form.modules,
      });
      toast.success("Usuário criado. Informe a senha inicial ao usuário por um canal seguro.");
      onOpenChange(false);
      onCreated(data);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="user-create-wizard">
        <DialogHeader>
          <DialogTitle>Novo usuário</DialogTitle>
        </DialogHeader>
        <ol className="mb-2 flex flex-wrap gap-2 text-xs" aria-label="Etapas do cadastro">
          {STEPS.map((label, index) => (
            <li key={label} className={`flex items-center gap-1 rounded-full border px-2.5 py-1 ${index === step ? "border-primary bg-primary/10 text-primary" : index < step ? "border-emerald-500/40 text-emerald-600" : "border-border text-muted-foreground"}`}>
              {index < step ? <Check size={12} /> : <span>{index + 1}</span>} {label}
            </li>
          ))}
        </ol>

        {step === 0 && (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="new-user-name">Nome completo</Label>
              <Input id="new-user-name" value={form.name || ""} onChange={(e) => set({ name: e.target.value })} data-testid="wizard-name" autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-user-email">E-mail (login)</Label>
              <Input id="new-user-email" type="email" value={form.email || ""} onChange={(e) => set({ email: e.target.value })} data-testid="wizard-email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-user-password">Senha inicial (mínimo 8 caracteres)</Label>
              <div className="flex gap-2">
                <Input id="new-user-password" value={form.password || ""} onChange={(e) => set({ password: e.target.value })} data-testid="wizard-password" />
                <Button type="button" variant="outline" onClick={() => set({ password: strongPassword() })}>Gerar</Button>
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Empresa cliente</Label>
              <Select value={form.client_id || ""} onValueChange={(value) => set({ client_id: value, unit_ids: [] })}>
                <SelectTrigger data-testid="wizard-client"><SelectValue placeholder="Selecione a empresa" /></SelectTrigger>
                <SelectContent>
                  {clients.map((client) => <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.client_id && (
              <div className="space-y-1.5">
                <Label>Unidades (nenhuma marcada = todas as unidades da empresa)</Label>
                <div className="grid max-h-48 grid-cols-1 gap-1.5 overflow-y-auto sm:grid-cols-2">
                  {units.map((unit) => (
                    <label key={unit.id} className="flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5 text-sm">
                      <Checkbox
                        checked={form.unit_ids.includes(unit.id)}
                        onCheckedChange={(checked) => set({ unit_ids: checked ? [...form.unit_ids, unit.id] : form.unit_ids.filter((id) => id !== unit.id) })}
                        data-testid={`wizard-unit-${unit.id}`}
                      />
                      {unit.name}
                    </label>
                  ))}
                  {!units.length && <p className="text-xs text-muted-foreground">Empresa sem unidades cadastradas.</p>}
                </div>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-1.5">
            <Label>Perfil</Label>
            <Select value={form.role || ""} onValueChange={(value) => set({ role: value, modules: {} })}>
              <SelectTrigger data-testid="wizard-role"><SelectValue placeholder="Selecione o perfil" /></SelectTrigger>
              <SelectContent>
                {roles.map((role) => <SelectItem key={role} value={role}>{ROLE_LABELS[role] || role}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Somente os perfis que você pode delegar são listados.</p>
          </div>
        )}

        {step === 3 && (
          <UserModuleGrants
            role={form.role}
            clientIds={form.client_id ? [form.client_id] : []}
            value={form.modules}
            onChange={(modules) => set({ modules })}
          />
        )}

        {step === 4 && (
          <dl className="grid grid-cols-3 gap-y-2 text-sm" data-testid="wizard-review">
            <dt className="text-muted-foreground">Nome</dt><dd className="col-span-2">{form.name}</dd>
            <dt className="text-muted-foreground">E-mail</dt><dd className="col-span-2">{form.email}</dd>
            <dt className="text-muted-foreground">Empresa</dt><dd className="col-span-2">{clientName || "—"}</dd>
            <dt className="text-muted-foreground">Unidades</dt><dd className="col-span-2">{form.unit_ids.length ? `${form.unit_ids.length} selecionada(s)` : "Todas"}</dd>
            <dt className="text-muted-foreground">Perfil</dt><dd className="col-span-2">{ROLE_LABELS[form.role] || form.role}</dd>
            <dt className="text-muted-foreground">Módulos</dt>
            <dd className="col-span-2">{Object.values(form.modules || {}).filter(Boolean).length} selecionado(s)</dd>
          </dl>
        )}

        <div className="mt-4 flex justify-between">
          <Button variant="outline" onClick={() => (step === 0 ? onOpenChange(false) : setStep(step - 1))}>
            {step === 0 ? "Cancelar" : "Voltar"}
          </Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={() => setStep(step + 1)} disabled={!valid[step]} data-testid="wizard-next">Avançar</Button>
          ) : (
            <Button onClick={submit} disabled={saving} data-testid="wizard-submit">Criar usuário</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminUsers() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [users, setUsers] = useState(null);
  const [clients, setClients] = useState([]);
  const [roles, setRoles] = useState([]);
  const [search, setSearch] = useState("");
  const [clientFilter, setClientFilter] = useState(params.get("empresa") || "all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const createOpen = params.get("novo") === "1";

  const load = useCallback(() => {
    api.get("/users").then(({ data }) => setUsers(Array.isArray(data) ? data : data.items || [])).catch((err) => {
      setUsers([]);
      toast.error(apiErrorMessage(err));
    });
  }, []);

  useEffect(() => {
    load();
    api.get("/clients").then(({ data }) => setClients(Array.isArray(data) ? data : data.items || [])).catch(() => {});
    api.get("/permissions/me/delegation").then(({ data }) => setRoles(data.allowed_target_roles || [])).catch(() => {});
  }, [load]);

  const clientNames = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c.name])), [clients]);
  const filtered = useMemo(() => {
    const text = search.trim().toLowerCase();
    return (users || []).filter((user) => {
      if (text && !`${user.name} ${user.email}`.toLowerCase().includes(text)) return false;
      if (clientFilter !== "all" && !userClientIds(user).includes(clientFilter)) return false;
      if (roleFilter !== "all" && user.role !== roleFilter) return false;
      if (statusFilter !== "all" && userStatus(user).label.toLowerCase() !== statusFilter) return false;
      return true;
    });
  }, [users, search, clientFilter, roleFilter, statusFilter]);

  const setCreateOpen = (value) => {
    const next = new URLSearchParams(params);
    if (value) next.set("novo", "1");
    else next.delete("novo");
    setParams(next);
  };

  return (
    <div className="space-y-5" data-testid="admin-users">
      <PageHeader
        icon={Users}
        title="Usuários e acessos"
        description="Cadastro, vínculos, módulos e permissões. Você só vê e administra usuários do seu escopo."
        actions={roles.length > 0 && (
          <Button onClick={() => setCreateOpen(true)} data-testid="user-create-btn">
            <Plus size={15} className="mr-1" /> Novo usuário
          </Button>
        )}
      />
      <div className="flex flex-col flex-wrap gap-2 sm:flex-row">
        <SearchBox value={search} onChange={setSearch} placeholder="Nome ou e-mail" testId="user-search" />
        <Select value={clientFilter} onValueChange={setClientFilter}>
          <SelectTrigger className="sm:w-52" aria-label="Filtrar por empresa" data-testid="user-client-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as empresas</SelectItem>
            {clients.map((client) => <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="sm:w-48" aria-label="Filtrar por perfil"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os perfis</SelectItem>
            {Object.entries(ROLE_LABELS).map(([key, label]) => <SelectItem key={key} value={key}>{label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="sm:w-36" aria-label="Filtrar por status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="ativo">Ativos</SelectItem>
            <SelectItem value="inativo">Inativos</SelectItem>
            <SelectItem value="bloqueado">Bloqueados</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        {users === null ? <LoadingState /> : filtered.length === 0 ? (
          <EmptyState title="Nenhum usuário encontrado" testId="users-empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Usuário</th>
                  <th className="px-4 py-3 font-medium">Perfil</th>
                  <th className="hidden px-4 py-3 font-medium md:table-cell">Empresas</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="hidden px-4 py-3 font-medium lg:table-cell">Último acesso</th>
                  <th className="px-4 py-3 text-right font-medium">Permissões</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((user) => {
                  const status = userStatus(user);
                  const names = userClientIds(user).map((id) => clientNames[id]).filter(Boolean);
                  return (
                    <tr key={user.id} className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-accent/40"
                      onClick={() => navigate(`/admin/usuarios/${user.id}`)} data-testid={`user-row-${user.email}`}>
                      <td className="px-4 py-3">
                        <Link to={`/admin/usuarios/${user.id}`} className="font-medium hover:underline" onClick={(e) => e.stopPropagation()}>{user.name}</Link>
                        <p className="text-xs text-muted-foreground">{user.email}</p>
                      </td>
                      <td className="px-4 py-3 text-xs">{ROLE_LABELS[user.role] || user.role}</td>
                      <td className="hidden px-4 py-3 text-xs text-muted-foreground md:table-cell">
                        {names.length ? `${names.slice(0, 2).join(", ")}${names.length > 2 ? ` +${names.length - 2}` : ""}` : "—"}
                      </td>
                      <td className="px-4 py-3"><StatePill tone={status.tone}>{status.label}</StatePill></td>
                      <td className="hidden px-4 py-3 text-xs text-muted-foreground lg:table-cell">{user.last_access ? formatDate(user.last_access) : "Nunca"}</td>
                      <td className="px-4 py-3 text-right">
                        {/* GESTÃO DE USUÁRIOS x PERMISSÕES: esta tela cuida da
                            conta; a concessão/revogação acontece na Central
                            Única de Permissões (mesmo serviço canônico). */}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/permissoes/usuarios/${user.id}`);
                          }}
                          data-testid={`user-manage-permissions-${user.email}`}
                        >
                          Gerenciar permissões
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <CreateUserWizard
        open={createOpen}
        onOpenChange={setCreateOpen}
        clients={clients}
        roles={roles}
        defaultClient={params.get("empresa") || ""}
        onCreated={(created) => navigate(`/admin/usuarios/${created.id}`)}
      />
    </div>
  );
}
